// A room on this screen: make one, join one by its code, and leave. While the
// screen is in a room, the AI plays no side, each action of this screen goes
// to the server, and each action of the other screen comes from it. See
// `state/roomMatch.ts` for the match, and `room/protocol.ts` for the messages.

import { ROOMS_PATH, STORAGE_KEY_ROOM_TOKEN_PREFIX } from '../constants';
import type { CasterId } from '../content/casters';
import { readText, writeText } from '../lib/storage';
import { type ClientMessage, type ServerMessage, sideOfSeat } from '../room/protocol';
import { openRoomSocket, type RoomSocket } from '../room/roomSocket';
import { setAiSides } from './aiDriver';
import { setActionSink } from './game';
import { localSideStore, spectatingStore } from './localSide';
import { matchWaitStore } from './matchWait';
import { closeCasterSelect, startFirstMatch } from './newMatch';
import { restoreSavedMatch } from './persist';
import {
  endRoomMatch,
  receiveAction,
  receiveSync,
  receiveView,
  startRoomMatchSync,
} from './roomMatch';
import { type RoomStatus, roomStore, updateRoom } from './roomStore';

/** The AI plays Moon in a match against the AI. */
const AI_SIDES_ALONE = ['violet'] as const;

/** The server ended the room for this screen. No later status replaces one of these. */
const FINAL_STATUSES: ReadonlySet<RoomStatus> = new Set(['gone', 'old', 'replaced']);

let socket: RoomSocket | null = null;

/** The token of the seat of this browser in the room. A new room gets a new one. */
const tokenFor = (code: string): string => {
  const key = `${STORAGE_KEY_ROOM_TOKEN_PREFIX}${code}`;
  const known = readText(key, '');
  if (known) return known;
  const token = crypto.randomUUID();
  writeText(key, token);
  return token;
};

const send = (message: ClientMessage): void => socket?.send(message);

const receive = (message: ServerMessage): void => {
  if (message.t === 'welcome') {
    updateRoom({ seat: message.seat });
    spectatingStore.set(message.seat === null);
  } else if (message.t === 'room') {
    const room = roomStore.get();
    if (!room) return;
    const seat = room.seat;
    // A spectator sees the board as Sun does.
    if (message.room.match) {
      localSideStore.set(seat ? sideOfSeat(message.room.match, seat) : 'gold');
    }
    // The vote of this screen is spent when the room goes back to the picks.
    const votedRematch = room.votedRematch && message.room.phase === 'playing';
    updateRoom({ view: message.room, votedRematch });
    receiveView(message.room);
  } else if (message.t === 'sync') {
    receiveSync(message.match);
  } else if (message.t === 'action') {
    receiveAction(message.index, message.action);
  } else if (message.t === 'refused') {
    console.warn(`The room refused: ${message.reason}`);
  } else {
    updateRoom({ status: message.t });
  }
};

/** Opens the room of `code` on this screen. The match against the AI waits until the screen leaves. */
export const joinRoom = (code: string): void => {
  if (roomStore.get()?.code === code) return;
  leaveSocket();
  window.history.replaceState(null, '', `/${code}`);
  roomStore.set({ code, status: 'connecting', seat: null, view: null, votedRematch: false });
  closeCasterSelect();
  // Nobody plays the match in the store until the room starts one.
  matchWaitStore.set(true);
  setAiSides([]);
  startRoomMatchSync(send);
  setActionSink((index, action) => send({ t: 'act', index, action }));
  socket = openRoomSocket(code, tokenFor(code), receive, (status) => {
    // The socket closes after the server ends the room for this screen. The reason stays.
    if (!FINAL_STATUSES.has(roomStore.get()?.status ?? 'open')) updateRoom({ status });
  });
};

/** Makes a room and joins it. Null and a reason when the server makes none. */
export const createRoom = async (): Promise<string | null> => {
  try {
    const response = await fetch(ROOMS_PATH, { method: 'POST' });
    if (!response.ok) return null;
    const { code } = (await response.json()) as { code?: string };
    if (!code) return null;
    joinRoom(code);
    return code;
  } catch {
    return null;
  }
};

const leaveSocket = (): void => {
  socket?.close();
  socket = null;
};

/**
 * Leaves the room and gives the seat up. A match in play is conceded. The
 * screen goes back to its match against the AI, or to a new one.
 */
export const leaveRoom = (): void => {
  send({ t: 'leave' });
  leaveSocket();
  roomStore.set(null);
  endRoomMatch();
  setActionSink(null);
  localSideStore.set('gold');
  spectatingStore.set(false);
  window.history.replaceState(null, '', '/');
  // The match against the AI comes back while the wait holds, so the save does not write
  // the match of the room over it.
  if (restoreSavedMatch()) matchWaitStore.set(false);
  else startFirstMatch();
  setAiSides(AI_SIDES_ALONE);
};

export const pickRoomCaster = (caster: CasterId): void => send({ t: 'pick', caster });
export const pickRoomRandom = (): void => send({ t: 'random' });
export const focusRoomCaster = (caster: CasterId | null): void => send({ t: 'focus', caster });
export const setRoomReady = (ready: boolean): void => send({ t: 'ready', ready });
export const claimRoomWin = (): void => send({ t: 'claim' });
export const takeRoomSeat = (): void => send({ t: 'sit' });

export const voteRoomRematch = (): void => {
  updateRoom({ votedRematch: true });
  send({ t: 'rematch' });
};
